import { useStyletron } from 'baseui';
import { Block } from 'baseui/block';
import { Button, SHAPE } from 'baseui/button';
import { Checkbox, STYLE_TYPE } from 'baseui/checkbox';
import { Check, ChevronLeft, ChevronRight } from 'baseui/icon';
import { Tab, Tabs } from 'baseui/tabs-motion';
import { HeadingXLarge } from 'baseui/typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Slider from "react-slick";
import { loadChallengeFeed, loadFavoriteChallenges, loadPublicChallengeFeed } from '../application/actions/challenges';
import { loadFavoriteSolutions, loadPublicSolutionFeed, loadSolutionById, loadSolutionFeed, loadSolutionsForChallenge } from '../application/actions/solutions';
import { routeTo, setPortalElement } from '../application/actions/ui';
import useBreakpoint from '../hooks/useBreakpoint';
import useWhoAmI from '../hooks/useWhoAmI';
import useLocale from '../hooks/useLocale';
import { HiGlobe } from 'react-icons/hi';
import ReDoProjectCard from './ReDoProjectCard';
import { DURATION, useSnackbar } from 'baseui/snackbar';

const isChallengeSolution = (project, challengeTitles = new Set()) => !!(
  project?.challengeId ||
  project?.challengeID ||
  project?.challenge_id ||
  project?.challenge ||
  project?.isChallengeSolution ||
  project?.projectType === 'challengeSolution' ||
  project?.visibility === 'company' ||
  challengeTitles.has(project?.what) ||
  challengeTitles.has(project?.challengeTitle)
);

const mergeFeedWithFavorites = (feed = [], favorites = []) => {
  const projectsById = new Map();

  feed
    .filter(project => project?.title)
    .forEach(project => projectsById.set(project.id, project));

  favorites
    .filter(project => project?.title)
    .forEach(project => projectsById.set(project.id, { ...projectsById.get(project.id), ...project, isFavorite: true }));

  return [...projectsById.values()];
}

const NAVBAR_HEIGHT = 68;

const ReDoExploreTab = ({ target, className, mode, isPublic, ...props }) => {
  const dispatch = useDispatch();
  const [css, theme] = useStyletron();
  const [checkboxes, setCheckboxes] = useState(false);
  const [data, setData] = useState([])
  const breakpoint = useBreakpoint()
  const [activeKey, setActiveKey] = useState("0");
  const { isCompany } = useWhoAmI();
  const { t, language, changeLanguage } = useLocale();

  const { enqueue } = useSnackbar();

  // Guards against a slow feed response from an earlier toggle overwriting the data
  // of the feed the user has since switched to.
  const requestSeq = useRef(0)

  const arrowSize = breakpoint === 'large' ? '64px' : '48px';
  const arrowIconSize = breakpoint === 'large' ? 64 : 48;
  const cardHeight = breakpoint === 'large' ? '550px' : ['300px', '350px', '400px', '550px'];
  const cardWidth = breakpoint === 'large' ? '375px' : '100%';

  const SamplePrevArrow = (props) => {
    const { onClick } = props;
    return (
      <Button onClick={onClick} overrides={{ BaseButton: { style: { zIndex: '2', position: 'absolute', top: '50%', left: '2.5%', transform: 'translateY(-50%)', backgroundColor: theme.colors.secondary200, height: arrowSize, width: arrowSize } } }} kind="tertiary" shape={SHAPE.circle}>
        <ChevronLeft size={arrowIconSize} />
      </Button>
    );
  }

  const SampleNextArrow = (props) => {
    const { onClick } = props;
    return (
      <Button onClick={onClick} overrides={{ BaseButton: { style: { zIndex: '2', position: 'absolute', top: '50%', right: '2.5%', transform: 'translateY(-50%)', backgroundColor: theme.colors.secondary200, height: arrowSize, width: arrowSize } } }} kind="tertiary" shape={SHAPE.circle}>
        <ChevronRight size={arrowIconSize} />
      </Button>

    );
  }

  const settings = {
    className: "feed center",
    centerMode: data.length > 1,
    infinite: data.length > 3,
    centerPadding: "64px",
    slidesToShow: Math.min(data.length, 3),
    speed: 500,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />
  };

  const navigateToProject = (project) => {
    // Logged-out visitors get the register/login gate instead of the detail view.
    if (isPublic) {
      dispatch(setPortalElement({ element: 'authGate' }))
      return
    }
    if (checkboxes || activeKey === '1') {
      dispatch(routeTo({ view: 'challenge', payload: { challenge: project } }))
    } else {
      dispatch(loadSolutionById(project.id)).then(sol => {
        dispatch(routeTo({ view: 'solution', payload: { solution: sol } }))
      })
    }
  }

  const loadExploreData = useCallback((projectType) => {
    const seq = ++requestSeq.current
    // Only the most recent request may write state, so a stale response from a
    // previously-selected feed can't clobber the one the user is now viewing.
    const apply = (rows) => { if (seq === requestSeq.current) setData(rows) }

    // Public (logged-out) path: fetch the unauthenticated feeds and skip every
    // authed enrichment (favorites, challenge-solution exclusion) so nothing throws.
    if (isPublic) {
      const loadFeed = projectType === 'challenge' ? loadPublicChallengeFeed : loadPublicSolutionFeed;
      return dispatch(loadFeed).then((feed) => {
        apply((feed || []).filter(project => project?.title))
      })
    }

    const loadFeed = projectType === 'challenge' ? loadChallengeFeed : loadSolutionFeed;
    const loadFavorites = projectType === 'challenge' ? loadFavoriteChallenges : loadFavoriteSolutions;

    return Promise.all([
      dispatch(loadFeed),
      dispatch(loadFavorites)
    ]).then(async ([feed, favorites]) => {
      const mergedProjects = mergeFeedWithFavorites(feed, favorites);
      let visibleProjects = mergedProjects;

      if (projectType === 'solution' && !isCompany) {
        let challengeTitles = new Set();
        let challengeSolutionIds = new Set();

        try {
          const challenges = await dispatch(loadChallengeFeed);
          challengeTitles = new Set(challenges.map(challenge => challenge.title).filter(Boolean));
          const challengeSolutionResponses = await Promise.allSettled(
            challenges.map(challenge => dispatch(loadSolutionsForChallenge(challenge.id)))
          );
          challengeSolutionIds = new Set(
            challengeSolutionResponses
              .filter(result => result.status === 'fulfilled')
              .flatMap(result => result.value || [])
              .map(solution => solution.id)
          );
        } catch (e) { }

        visibleProjects = mergedProjects.filter(project =>
          !challengeSolutionIds.has(project.id) && !isChallengeSolution(project, challengeTitles)
        );
      }

      apply(visibleProjects);
    })
  }, [dispatch, isCompany, isPublic])

  const onToggle = (e) => {
    setCheckboxes(e.currentTarget.checked)
    if (e.currentTarget.checked) {
      loadExploreData('challenge')
    } else {
      loadExploreData('solution')
    }
  }

  const onFavorite = (updatedProject) => {
    enqueue({ message: updatedProject?.isFavorite ? t('common.saved_to_favorites') : t('common.removed_from_favorites'), dismissAfter: DURATION.shortest, startEnhancer: () => <Check size={theme.sizing.scale800} /> })
    setData(prev => prev.map(item => item.id === updatedProject?.id ? { ...item, ...updatedProject } : item))
  }

  const onTabChange = ({ activeKey }) => {
    setActiveKey(activeKey)
    if (activeKey === '0') {
      loadExploreData('solution')
    } else {
      loadExploreData('challenge')
    }
  }

  useEffect(() => {
    loadExploreData('solution')
  }, [loadExploreData])

  return (
    <>
      {breakpoint === 'large' &&
        <Block display='flex' flexDirection='column' height='100%' padding={`0 ${theme.sizing.scale850}`}>

          <Block paddingTop={['2ch', '3ch', '4ch', '6ch']} height='100%' display='flex' gridGap={['1ch', '1ch', '2ch', '3ch']}>

            <Block display='flex' flexDirection='column' flex='0 0 auto' minWidth='200px' maxWidth='250px'>
              <HeadingXLarge overrides={{ Block: { style: { lineHeight: '48px', fontWeight: '300' } } }} paddingTop='1ch' margin='0'>{t('explore.heading')}</HeadingXLarge>

              {!mode &&
                <Block paddingTop='3ch' display='flex' alignItems='center' gridGap='1ch'>
                  {t('explore.toggle_solutions')}
                  <Checkbox
                    checked={checkboxes}
                    onChange={onToggle}
                    checkmarkType={STYLE_TYPE.toggle_round}
                  />
                  {t('explore.toggle_challenges')}
                </Block>
              }

            </Block>

            <Block position='relative' height='100%' flex='1' minWidth='0'>

              {!!data.length &&
                <Block display='flex' flexDirection='column' justifyContent='center' height='100%' width='100%'>
                  <Slider key={JSON.stringify(data)} {...settings}>
                    {data.map((project, i) => (
                      <Block height={cardHeight} width={cardWidth} display='flex' justifyContent='center'>
                        <ReDoProjectCard key={project.id} onFavorite={onFavorite} onClick={navigateToProject} project={project} isPublic={isPublic} />
                      </Block>
                    ))}
                  </Slider>
                </Block>
              }

            </Block>

            <Block display='flex' flexDirection='column' flex='0 0 auto' minWidth='80px' />

          </Block>

        </Block>
      }

      {breakpoint !== 'large' &&
        <>
          {!mode && <div className="explore">
            <Block display='flex' alignItems='center' justifyContent='space-between'>
              <Tabs
                overrides={{
                  Root: {
                    style: ({ $theme }) => ({
                      backgroundColor: 'transparent'
                    })
                  },
                  TabBorder: {
                    style: ({ $theme }) => ({
                      backgroundColor: 'transparent'
                    })
                  },
                  TabHighlight: {
                    style: () => ({
                      height: '2px',
                      minWidth: '88px',
                    })
                  },
                  TabList: {
                    style: () => ({
                      gap: '2ch'
                    })
                  }
                }}
                activeKey={activeKey}
                onChange={onTabChange}
                activateOnFocus
              >

                <Tab
                  overrides={{
                    Tab: {
                      style: () => ({
                        background: 'transparent',
                        fontSize: '18px',
                        fontWeight: activeKey === '0' ? 'bold' : 'regular',
                        padding: '2px',
                        ':hover': {
                          background: 'transparent',
                        }
                      })
                    },
                  }}
                  title={t('explore.tab_solutions')}
                />

                <Tab
                  overrides={{
                    Tab: {
                      style: () => ({
                        background: 'transparent',
                        fontSize: '18px',
                        fontWeight: activeKey === '1' ? 'bold' : 'regular',
                        padding: '2px',
                        ':hover': {
                          background: 'transparent',
                        }
                      })
                    }
                  }}
                  title={t('explore.tab_challenges')}
                />

              </Tabs>
              <button
                onClick={() => changeLanguage(language.startsWith('de') ? 'en' : 'de')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '6px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontWeight: '600',
                  fontSize: '13px',
                  letterSpacing: '0.03em',
                  flexShrink: 0,
                }}
              >
                <HiGlobe size={20} />
                {language.startsWith('de') ? 'DE' : 'EN'}
              </button>
            </Block>
          </div>}

          <Block display='flex' flexDirection='column' gridGap={['1ch', '1.5ch', '2ch']} marginTop={['0.5ch', '1ch', '1ch']} paddingBottom={isPublic ? '2ch' : `calc(${NAVBAR_HEIGHT}px + 2ch)`} overflowY='auto' overflowX='hidden' flex='1' minHeight='0' width='100%'>
            {data.map((project) => (
              <Block key={project.id} height={cardHeight} width='100%' flexShrink='0'>
                <ReDoProjectCard onFavorite={onFavorite} onClick={navigateToProject} project={project} isPublic={isPublic} />
              </Block>
            ))}
          </Block>
        </>
      }
    </>
  );
};

export default ReDoExploreTab;
